import { Component } from '@angular/core';
import { FormGroup, FormBuilder, Validators, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { finalize, Subscription } from 'rxjs';
import { XCourseFilter } from 'src/app/core/common/XCourseFilter';
import { SyncSource } from 'src/app/core/sync-source';
import { ApiService } from 'src/app/services/api.service';
import { RealtimeService } from 'src/app/services/realtime.service';

@Component({
  selector: 'app-manual-sync',
  templateUrl: './manual-sync.component.html',
  styleUrl: './manual-sync.component.scss'
})
export class ManualSyncComponent {
  // Sync source list
  sources: SyncSource[] = [];

  // Selected source IDs
  selected = new Set<number>();

  // Page states
  loading = true;
  adding = false;
  notice = '';

  // Pagination
  currentPage = 1;
  pageSize = 10;
  readonly pageSizeOptions: number[] = [10, 20, 50];

  // Subscriptions
  sub?: Subscription;

  // Add links form
  form: FormGroup;

  // Edit source dialog state
  editForm: FormGroup;
  editingSource: SyncSource | null = null;
  savingEdit = false;
  editNotice = '';

  // Delete confirmation popup state.
  pendingDeleteSource: SyncSource | null = null;
  deletingSourceId: number | null = null;
  deleteNotice = '';


  // Search by course name or code.
  searchText: string = '';


  constructor(
    private api: ApiService,
    private realtime: RealtimeService,
    private fb: FormBuilder
  ) {
    // Initialize add links form
    this.form = this.fb.group({
      urls: ['', [Validators.required, this.urlListValidator()]],
      category: ['SHORT COURSES', [Validators.required, Validators.maxLength(100)]],
      priority: [100, [Validators.required, Validators.min(1), Validators.max(9999)]]
    });

    this.editForm = this.fb.group({
      url: ['', [Validators.required, this.singleUrlValidator()]],
      courseCode: [{
        value: '',
        disabled: true
      }, [Validators.maxLength(200)]],
      courseTitle: [{
        value: '',
        disabled: true
      }, [Validators.maxLength(500)]],
      category: [{
        value: '',
        disabled: true
      }, [Validators.required, Validators.maxLength(200)]],
      priority: [100, [Validators.required, Validators.min(1), Validators.max(9999)]],
      isActive: [true]
    });
  }

  /// <summary>
  /// Initialize dashboard data and realtime updates.
  /// </summary>
  ngOnInit(): void {
    // Load configured links
    this.load2();

    // Start SignalR connection
    this.realtime.start().catch(error => {
      console.error('Unable to start realtime connection.', error);
    });

    // Listen for realtime sync updates
    this.sub = this.realtime.updates$.subscribe(update => {
      const source = this.sources.find(s => s.id === update.sourceId);

      if (source) {
        source.status = update.status;
        source.statusMessage = update.message;
        source.lastOccurrenceCount = update.occurrenceCount;
        source.lastClassCount = update.classCount;
        source.lastUnitCount = update.unitCount;
      }

      // Refresh final values after a job finishes
      if (update.status === 'Completed' || update.status === 'Failed') {
        setTimeout(() => this.load(false), 400);
      }
    });
  }

  /// <summary>
  /// Dispose component subscriptions.
  /// </summary>
  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    document.body.style.overflow = '';
  }

  /// <summary>
  /// Load all configured sync sources.
  /// </summary>
  load(spin: boolean = true): void {
    if (spin) {
      this.loading = true;
    }

    this.api.getSources(this.searchText).subscribe({
      next: (result: SyncSource[]) => {
        this.sources = result ?? [];
        this.ensureValidPage();
        this.loading = false;
      },
      error: error => {
        console.error('Unable to load sync sources.', error);
        this.loading = false;
        this.notice = 'Unable to load sync sources.';
      }
    });
  }




  // ============================================================
  // LOAD SYNC SOURCES - SERVER-SIDE PAGINATION
  // ============================================================

  load2(spin: boolean = true): void {

    // Prepare filters
    this.filter.search = '';
    this.filter.searchKey = this.searchText || '';
    this.filter.pageSize = this.pageSize;
    this.filter.currentPage = this.currentPage;

    if (spin) {
      this.loading = true;
    }

    this.api
      .getAllSourcesList(this.filter)
      .pipe(
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({

        next: (response) => {

          // Records returned by SQL
          this.sources = response.records || [];

          // Total matching records
          this.totalRecords = response.totalRecords || 0;

          // Current page
          this.currentPage = response.currentPage || 1;

          this.ensureValidPage();
        },

        error: (error) => {

          console.error(
            'Unable to load sync sources.',
            error
          );

          this.sources = [];
          this.totalRecords = 0;

          this.notice = 'Unable to load sync sources.';
        }
      });
  }


  /// <summary>
  /// Add one or more VETEnrol links.
  /// </summary>
  add(): void {
    // Validate form
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.notice = 'Please correct the highlighted validation errors.';
      return;
    }

    // Prepare URL list
    const rawUrls: string = this.form.value.urls ?? '';
    const urls: string[] = this.parseUrls(rawUrls);

    // Validate duplicate entries against the existing list
    const existingUrls = new Set(
      this.sources.map(source => this.normaliseUrl(source.url))
    );

    const duplicateExistingUrls = urls.filter(url =>
      existingUrls.has(this.normaliseUrl(url))
    );

    if (duplicateExistingUrls.length > 0) {
      this.notice = 'One or more entered links already exist in the configured list.';
      return;
    }

    // Program IDs must also be unique. Different URLs pointing to the same
    // VETEnrol progid would otherwise update the same course twice.
    const enteredProgramIds = urls
      .map(url => this.getProgramId(url))
      .filter((id): id is number => id !== null);

    if (new Set(enteredProgramIds).size !== enteredProgramIds.length) {
      this.notice = 'The same VETEnrol progid has been entered more than once.';
      return;
    }

    const existingProgramIds = new Set(
      this.sources
        .map(source => source.programId)
        .filter((id): id is number => id != null)
    );

    const duplicateProgramId = enteredProgramIds.find(id =>
      existingProgramIds.has(id)
    );

    if (duplicateProgramId != null) {
      this.notice = `VETEnrol program #${duplicateProgramId} is already configured.`;
      return;
    }

    // Prepare request
    const category: string = (this.form.value.category ?? 'SHORT COURSES').trim();
    const priority: number = Number(this.form.value.priority ?? 100);

    const items = urls.map((url: string) => ({
      url,
      category,
      priority,
      isActive: true
    }));

    // Add sync sources
    this.adding = true;
    this.notice = '';

    this.api.addSources(items).subscribe({
      next: () => {
        this.form.patchValue({ urls: '' });
        this.adding = false;
        this.notice = `Added ${items.length} link(s).`;
        this.currentPage = 1;
        this.load2();
      },
      error: (error: any) => {
        console.error('Unable to add URLs.', error);
        this.adding = false;
        this.notice =
          error?.error?.title ??
          error?.error?.message ??
          error?.message ??
          'Unable to add links.';
      }
    });
  }

  /// <summary>
  /// Open the edit dialog for an existing source.
  /// </summary>
  openEdit(source: SyncSource): void {
    this.editingSource = source;
    this.editNotice = '';

    this.editForm.reset({
      url: source.url || '',
      courseCode: source.courseCode || '',
      courseTitle: source.courseTitle || '',
      category: source.category || 'SHORT COURSES',
      priority: source.priority || 100,
      isActive: source.isActive !== false
    });
  }

  /// <summary>
  /// Close the edit dialog.
  /// </summary>
  closeEdit(): void {
    if (this.savingEdit) {
      return;
    }

    this.editingSource = null;
    this.editNotice = '';
  }

  /// <summary>
  /// Save an edited source. When runAfterSave is true the saved source is
  /// immediately queued so the SAME linked ECB short-course row is refreshed.
  /// </summary>
  saveEdit(runAfterSave: boolean = false): void {
    if (!this.editingSource || this.savingEdit) {
      return;
    }

    if (this.editForm.invalid) {
      this.editForm.markAllAsTouched();
      this.editNotice = 'Please correct the highlighted validation errors.';
      return;
    }

    const url = String(this.editForm.value.url || '').trim();
    const courseCode = String(this.editForm.value.courseCode || '').trim();
    const courseTitle = String(this.editForm.value.courseTitle || '').trim();
    const category = String(this.editForm.value.category || 'SHORT COURSES').trim();
    const priority = Number(this.editForm.value.priority || 100);
    const isActive = this.editForm.value.isActive !== false;

    const editingId = this.editingSource.id;
    const programId = this.getProgramId(url);

    if (programId == null) {
      this.editNotice = 'The VETEnrol URL must contain a numeric progid.';
      return;
    }

    // Same URL / progid / course code on the row being edited is valid.
    // Only OTHER rows are checked for duplicates.
    const otherSources = this.sources.filter(source => source.id !== editingId);

    if (otherSources.some(source =>
      this.normaliseUrl(source.url) === this.normaliseUrl(url)
    )) {
      this.editNotice = 'This VETEnrol URL is already configured on another source.';
      return;
    }

    if (otherSources.some(source => Number(source.programId) === programId)) {
      this.editNotice = `VETEnrol program #${programId} is already configured on another source.`;
      return;
    }

    if (courseCode) {
      const courseCodeKey = this.normaliseCourseCode(courseCode);

      if (otherSources.some(source =>
        this.normaliseCourseCode(source.courseCode || '') === courseCodeKey
      )) {
        this.editNotice = `Course code "${courseCode}" is already linked to another sync source.`;
        return;
      }
    }

    const payload: SyncSource = {
      ...this.editingSource,
      url,
      programId,
      courseCode,
      courseTitle,
      category,
      priority,
      isActive
    };

    this.savingEdit = true;
    this.editNotice = '';

    this.api.updateSource(editingId, payload).subscribe({
      next: () => {
        if (!runAfterSave) {
          this.savingEdit = false;
          this.editingSource = null;
          this.notice = 'Sync source updated successfully.';
          this.load(false);
          return;
        }

        this.api.runOne(editingId).subscribe({
          next: () => {
            this.savingEdit = false;
            this.editingSource = null;
            this.notice = 'Sync source updated and queued. The linked short course will be refreshed.';
            this.load(false);
          },
          error: (error: any) => {
            this.savingEdit = false;
            this.editNotice =
              error?.error?.message ??
              error?.error?.title ??
              error?.message ??
              'The source was saved, but it could not be queued.';
          }
        });
      },
      error: (error: any) => {
        this.savingEdit = false;
        this.editNotice =
          error?.error?.message ??
          error?.error?.title ??
          error?.message ??
          'Unable to update the sync source.';
      }
    });
  }

  /// <summary>
  /// Return edit URL validation feedback.
  /// </summary>
  getEditUrlValidationMessage(): string {
    const control = this.editForm.get('url');

    if (!control || !control.touched || !control.errors) {
      return '';
    }

    if (control.errors['required']) {
      return 'Please enter a VETEnrol URL.';
    }

    if (control.errors['invalidUrl']) {
      return 'Enter one valid HTTPS VETEnrol URL.';
    }

    if (control.errors['invalidHost']) {
      return 'Only enrol.vetenrol.com.au links are allowed.';
    }

    if (control.errors['missingClientId']) {
      return 'The URL must contain clientid.';
    }

    if (control.errors['missingProgramId']) {
      return 'The URL must contain a numeric progid.';
    }

    return 'Please enter a valid VETEnrol URL.';
  }

  /// <summary>
  /// Select or unselect a sync source.
  /// </summary>
  toggleSelection(id: number): void {
    if (this.selected.has(id)) {
      this.selected.delete(id);
    } else {
      this.selected.add(id);
    }
  }

  /// <summary>
  /// Select or unselect all active rows on the current page.
  /// </summary>
  selectAll(): void {
    const activeSources = this.pagedSources.filter(source => source.isActive);
    const allSelected =
      activeSources.length > 0 &&
      activeSources.every(source => this.selected.has(source.id));

    if (allSelected) {
      activeSources.forEach(source => this.selected.delete(source.id));
    } else {
      activeSources.forEach(source => this.selected.add(source.id));
    }
  }

  /// <summary>
  /// Run selected sync sources with high priority.
  /// </summary>
  runSelected(): void {
    if (this.selected.size === 0) {
      this.notice = 'Please select at least one link.';
      return;
    }

    const ids: number[] = Array.from(this.selected);
    this.notice = 'Selected links have been queued.';

    this.api.runSelected(ids).subscribe({
      next: () => {
        this.load(false);
      },
      error: (error: any) => {
        console.error('Unable to run selected links.', error);
        this.notice =
          error?.error?.message ??
          error?.message ??
          'Unable to run selected links.';
      }
    });
  }

  /// <summary>
  /// Run all active sync sources.
  /// </summary>
  runAll(): void {
    if (this.activeCount === 0) {
      this.notice = 'There are no active links to run.';
      return;
    }

    this.notice = 'All active links have been queued.';

    this.api.runAll().subscribe({
      next: () => {
        this.load(false);
      },
      error: (error: any) => {
        console.error('Unable to run all links.', error);
        this.notice =
          error?.error?.message ??
          error?.message ??
          'Unable to run all links.';
      }
    });
  }

  /// <summary>
  /// Run a single sync source.
  /// </summary>
  runOne(id: number): void {
    const source = this.sources.find(item => item.id === id);

    if (!source || !source.isActive) {
      this.notice = 'Only active links can be run.';
      return;
    }

    this.notice = 'Link has been queued for synchronization.';

    this.api.runOne(id).subscribe({
      next: () => {
        this.load(false);
      },
      error: (error: any) => {
        console.error('Unable to run link.', error);
        this.notice =
          error?.error?.message ??
          error?.message ??
          'Unable to run link.';
      }
    });
  }

  /// <summary>
  /// Enable or disable a sync source.
  /// </summary>
  toggleActive(source: SyncSource): void {
    const newStatus = !source.isActive;

    this.api.setActive(source.id, newStatus).subscribe({
      next: () => {
        if (!newStatus) {
          this.selected.delete(source.id);
        }

        this.load(false);
      },
      error: (error: any) => {
        console.error('Unable to update active status.', error);
        this.notice =
          error?.error?.message ??
          error?.message ??
          'Unable to update active status.';
      }
    });
  }

  /// <summary>
  /// Open a proper confirmation popup before deleting a configured source.
  /// </summary>
  remove(source: SyncSource): void {
    if (this.deletingSourceId !== null) {
      return;
    }

    this.deleteNotice = '';
    this.pendingDeleteSource = source;
    document.body.style.overflow = 'hidden';
  }

  /// <summary>
  /// Close the delete confirmation popup.
  /// </summary>
  closeDeletePopup(): void {
    if (this.deletingSourceId !== null) {
      return;
    }

    this.pendingDeleteSource = null;
    this.deleteNotice = '';
    document.body.style.overflow = '';
  }

  /// <summary>
  /// Delete the source after the user confirms in the popup.
  /// </summary>
  confirmDeleteSource(): void {
    if (!this.pendingDeleteSource || this.deletingSourceId !== null) {
      return;
    }

    const source = this.pendingDeleteSource;
    this.deletingSourceId = source.id;
    this.deleteNotice = '';

    this.api.delete(source.id).subscribe({
      next: () => {
        this.selected.delete(source.id);
        this.deletingSourceId = null;
        this.pendingDeleteSource = null;
        this.notice = 'Link removed successfully.';
        document.body.style.overflow = '';
        this.load2();
      },
      error: (error: any) => {
        console.error('Unable to remove link.', error);

        this.deletingSourceId = null;

        this.deleteNotice =
          error?.error?.message ??
          error?.error?.title ??
          error?.message ??
          'Unable to remove link.';
      }
    });
  }


  /// <summary>
  /// Return the validation message for the URL field.
  /// </summary>
  getUrlValidationMessage(): string {
    const control = this.form.get('urls');

    if (!control || !control.touched || !control.errors) {
      return '';
    }

    if (control.errors['required']) {
      return 'Please enter at least one VETEnrol URL.';
    }

    if (control.errors['invalidUrl']) {
      return 'Every entry must be a valid HTTPS VETEnrol URL.';
    }

    if (control.errors['invalidHost']) {
      return 'Only enrol.vetenrol.com.au links are allowed.';
    }

    if (control.errors['missingClientId']) {
      return 'Every URL must contain a clientid value.';
    }

    if (control.errors['missingProgramId']) {
      return 'Every URL must contain a numeric progid value.';
    }

    if (control.errors['duplicateUrl']) {
      return 'The same URL has been entered more than once.';
    }

    return 'Please enter valid VETEnrol links.';
  }

  /// <summary>
  /// Validate all URLs entered in the multi-line URL field.
  /// </summary>
  private urlListValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const rawValue = String(control.value ?? '').trim();

      if (!rawValue) {
        return null;
      }

      const urls = this.parseUrls(rawValue);
      const normalisedUrls = urls.map(url => this.normaliseUrl(url));

      if (new Set(normalisedUrls).size !== normalisedUrls.length) {
        return { duplicateUrl: true };
      }

      for (const value of urls) {
        let parsedUrl: URL;

        try {
          parsedUrl = new URL(value);
        } catch {
          return { invalidUrl: true };
        }

        if (parsedUrl.protocol !== 'https:') {
          return { invalidUrl: true };
        }

        if (parsedUrl.hostname.toLowerCase() !== 'enrol.vetenrol.com.au') {
          return { invalidHost: true };
        }

        if (!parsedUrl.searchParams.get('clientid')) {
          return { missingClientId: true };
        }

        const programId = parsedUrl.searchParams.get('progid');

        if (!programId || !/^\d+$/.test(programId)) {
          return { missingProgramId: true };
        }
      }

      return null;
    };
  }

  /// <summary>
  /// Validate one edit URL.
  /// </summary>
  private singleUrlValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = String(control.value ?? '').trim();

      if (!value) {
        return null;
      }

      if (/\r?\n|,/.test(value)) {
        return { invalidUrl: true };
      }

      let parsedUrl: URL;

      try {
        parsedUrl = new URL(value);
      } catch {
        return { invalidUrl: true };
      }

      if (parsedUrl.protocol !== 'https:') {
        return { invalidUrl: true };
      }

      if (parsedUrl.hostname.toLowerCase() !== 'enrol.vetenrol.com.au') {
        return { invalidHost: true };
      }

      if (!parsedUrl.searchParams.get('clientid')) {
        return { missingClientId: true };
      }

      const programId = parsedUrl.searchParams.get('progid');

      if (!programId || !/^\d+$/.test(programId)) {
        return { missingProgramId: true };
      }

      return null;
    };
  }

  /// <summary>
  /// Read the numeric VETEnrol progid from a URL.
  /// </summary>
  private getProgramId(value: string): number | null {
    try {
      const parsed = new URL(value);
      const programValue = parsed.searchParams.get('progid');

      if (!programValue || !/^\d+$/.test(programValue)) {
        return null;
      }

      const id = Number(programValue);
      return Number.isFinite(id) && id > 0 ? id : null;
    } catch {
      return null;
    }
  }

  /// <summary>
  /// Normalise course-code text for duplicate comparison.
  /// </summary>
  private normaliseCourseCode(value: string): string {
    return (value || '')
      .replace(/[^a-z0-9]/gi, '')
      .toUpperCase();
  }

  /// <summary>
  /// Split comma/new-line separated URL values.
  /// </summary>
  private parseUrls(rawUrls: string): string[] {
    return rawUrls
      .split(/\r?\n|,/)
      .map((value: string) => value.trim())
      .filter((value: string) => value.length > 0);
  }

  /// <summary>
  /// Normalize URL text before duplicate comparison.
  /// </summary>
  private normaliseUrl(value: string): string {
    return value.trim().replace(/\/$/, '').toLowerCase();
  }

  /// <summary>
  /// Keep the current page within the available range.
  /// </summary>

  // Ensure valid page
  private ensureValidPage(): void {

    if (this.currentPage > this.totalPages) {

      this.currentPage = this.totalPages;

      this.load2();

      return;
    }

    if (this.currentPage < 1) {
      this.currentPage = 1;
    }
  }

  // Number of active links
  get activeCount(): number {
    return this.sources.filter(source => source.isActive).length;
  }

  // Number of selected links
  get selectedCount(): number {
    return this.selected.size;
  }


  // SQL already returns the requested page
  get pagedSources(): SyncSource[] {
    return this.sources;
  }


  // Total pages from database count
  get totalPages(): number {

    return Math.max(
      1,
      Math.ceil(this.totalRecords / this.pageSize)
    );
  }

  // Starting record number
  get pageStart(): number {

    if (this.totalRecords === 0) {
      return 0;
    }

    return ((this.currentPage - 1) * this.pageSize) + 1;
  }

  // Ending record number
  get pageEnd(): number {

    return Math.min(
      this.currentPage * this.pageSize,
      this.totalRecords
    );
  }


  // Page numbers
  get pageNumbers(): number[] {

    const maxButtons = 5;

    let startPage = Math.max(
      1,
      this.currentPage - 2
    );

    let endPage = Math.min(
      this.totalPages,
      startPage + maxButtons - 1
    );

    startPage = Math.max(
      1,
      endPage - maxButtons + 1
    );

    return Array.from(
      {
        length: endPage - startPage + 1
      },
      (_, index) => startPage + index
    );
  }

  // Change page
  changePage(page: number): void {

    if (
      page < 1 ||
      page > this.totalPages ||
      page === this.currentPage ||
      this.loading
    ) {
      return;
    }

    this.currentPage = page;

    // Retrieve new page from API
    this.load2();
  }

  // Change page size
 // Change page size
changePageSize(value: string | number): void {

    const newPageSize = Number(value);

    if (!this.pageSizeOptions.includes(newPageSize)) {
        return;
    }

    this.pageSize = newPageSize;
    this.currentPage = 1;

    // Retrieve new page from API
    this.load2();
}


  // Check if all active rows on the current page are selected
  get allPageSelected(): boolean {
    const activeSources = this.pagedSources.filter(source => source.isActive);

    return (
      activeSources.length > 0 &&
      activeSources.every(source => this.selected.has(source.id))
    );
  }

  // Create status CSS class
  statusClass(status: string): string {
    const normalizedStatus = (status || 'pending')
      .toLowerCase()
      .replace(/\s+/g, '-');

    return `status status-${normalizedStatus}`;
  }


  //search function
  onSearchChange(): void {

    this.currentPage = 1;
    this.ensureValidPage();
    this.load2();
  }





  totalRecords: number = 0;



  filter: XCourseFilter = {
    action: '',
    type: '',
    search: '',
    searchKey: '',
    isActive: null,
    stateCode: '',
    pageSize: 10,
    currentPage: 1
  };
}

